/**
 * LanguageSwitcher molecule (Story 11.2)
 * Header dropdown for switching application language.
 * Handles immediate UI update, backend persistence for auth'd users,
 * toast notification, RTL switching (via i18n.ts onLanguageChanged), and
 * full keyboard navigation.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../i18n";
import { useAuthStore } from "../../store/authStore";
import { profileService } from "../../services/profileService";

const LOCALE_CODES: Record<SupportedLocale, string> = {
  en: "EN",
  fr: "FR",
  ar: "AR",
};

const LOCALE_NATIVE_NAMES: Record<SupportedLocale, string> = {
  en: "English",
  fr: "Français",
  ar: "العربية",
};

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation("common");
  const { user, setUser } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  const rawLang = i18n.language?.slice(0, 2) as SupportedLocale;
  const currentLocale: SupportedLocale =
    SUPPORTED_LOCALES.includes(rawLang) ? rawLang : "en";

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Cleanup toast timer on unmount
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const handleSelect = useCallback(
    async (locale: SupportedLocale) => {
      setIsOpen(false);

      if (locale === currentLocale) return;

      // Immediate UI update — RTL switching handled automatically by i18n.ts
      await i18n.changeLanguage(locale);

      // Toast in the newly selected language
      const nativeName = LOCALE_NATIVE_NAMES[locale];
      const msg = t("language_changed_to", { language: nativeName });
      setToastMessage(msg);
      setToastVisible(true);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => setToastVisible(false), 3000);

      // Persist to backend (authenticated users only) — optimistic: UI already updated
      if (user) {
        try {
          const updated = await profileService.updateProfile({ locale });
          setUser({ ...user, locale: updated.locale ?? locale });
        } catch (err) {
          console.error("Failed to save locale preference:", err);
        }
      }
    },
    [currentLocale, i18n, user, setUser]
  );

  // Keyboard: trigger button handler (when dropdown is closed)
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(true);
      // Focus first option after open
      setTimeout(() => optionRefs.current[0]?.focus(), 0);
    }
  };

  // Keyboard: option list item handler
  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    locale: SupportedLocale,
    idx: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(locale);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(idx + 1, SUPPORTED_LOCALES.length - 1);
      optionRefs.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(idx - 1, 0);
      optionRefs.current[prev]?.focus();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select language"
        className="flex items-center gap-1 min-h-[44px] min-w-[44px] px-2 py-1 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800"
      >
        <span>{LOCALE_CODES[currentLocale]}</span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <ul
          role="listbox"
          aria-label="Select language"
          className="absolute end-0 mt-1 w-36 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 z-50 overflow-hidden"
        >
          {(SUPPORTED_LOCALES as readonly SupportedLocale[]).map((locale, idx) => (
            <li
              key={locale}
              ref={(el) => {
                optionRefs.current[idx] = el;
              }}
              role="option"
              aria-selected={locale === currentLocale}
              tabIndex={0}
              onClick={() => handleSelect(locale)}
              onKeyDown={(e) => handleOptionKeyDown(e, locale, idx)}
              className="flex items-center justify-between min-h-[44px] px-4 py-2 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:bg-neutral-50 dark:focus:bg-neutral-700"
            >
              <span>{LOCALE_NATIVE_NAMES[locale]}</span>
              {locale === currentLocale && (
                <CheckIcon
                  className="w-4 h-4 text-primary-600 dark:text-primary-400"
                  aria-hidden="true"
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Toast notification */}
      {toastVisible && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-fab-offset start-1/2 -translate-x-1/2 md:bottom-6 z-50 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2"
        >
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}
